// Lightweight Markdown → HTML converter (no deps). Handles the subset the
// product-builder AI actually outputs: headings, bold, italic, links, lists,
// blockquotes, horizontal rules, code (inline + fenced), paragraphs.

const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

function renderInline(text) {
  // Escape first, then apply simple inline rules.
  let t = escapeHtml(text);
  // Links [text](url)
  t = t.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noreferrer">$1</a>'
  );
  // Bold **text** or __text__
  t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  // Italic *text* or _text_
  t = t.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  t = t.replace(/(^|[^_])_([^_\n]+)_/g, "$1<em>$2</em>");
  // Inline code `code`
  t = t.replace(/`([^`\n]+)`/g, "<code>$1</code>");
  return t;
}

export function mdToHtml(md = "") {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let inList = null; // "ul" | "ol" | null
  let inCode = false;
  let codeBuf = [];
  let paraBuf = [];

  const flushPara = () => {
    if (paraBuf.length) {
      out.push(`<p>${renderInline(paraBuf.join(" ").trim())}</p>`);
      paraBuf = [];
    }
  };
  const flushList = () => {
    if (inList) {
      out.push(`</${inList}>`);
      inList = null;
    }
  };

  for (let raw of lines) {
    // Fenced code block toggle
    if (/^```/.test(raw)) {
      if (inCode) {
        out.push(
          `<pre><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`
        );
        codeBuf = [];
        inCode = false;
      } else {
        flushPara();
        flushList();
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeBuf.push(raw);
      continue;
    }

    const line = raw.trim();

    // Empty line → flush paragraph / list
    if (!line) {
      flushPara();
      flushList();
      continue;
    }

    // Headings
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flushPara();
      flushList();
      const level = h[1].length;
      out.push(`<h${level}>${renderInline(h[2])}</h${level}>`);
      continue;
    }

    // Horizontal rule
    if (/^[-*_]{3,}$/.test(line)) {
      flushPara();
      flushList();
      out.push("<hr />");
      continue;
    }

    // Blockquote
    const bq = line.match(/^>\s?(.*)$/);
    if (bq) {
      flushPara();
      flushList();
      out.push(`<blockquote>${renderInline(bq[1])}</blockquote>`);
      continue;
    }

    // Ordered list
    const ol = line.match(/^(\d+)\.\s+(.*)$/);
    if (ol) {
      flushPara();
      if (inList !== "ol") {
        flushList();
        out.push("<ol>");
        inList = "ol";
      }
      out.push(`<li>${renderInline(ol[2])}</li>`);
      continue;
    }

    // Unordered list
    const ul = line.match(/^[-*+]\s+(.*)$/);
    if (ul) {
      flushPara();
      if (inList !== "ul") {
        flushList();
        out.push("<ul>");
        inList = "ul";
      }
      out.push(`<li>${renderInline(ul[1])}</li>`);
      continue;
    }

    // Paragraph
    flushList();
    paraBuf.push(line);
  }

  if (inCode) {
    out.push(`<pre><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`);
  }
  flushPara();
  flushList();

  return out.join("\n");
}

// Full beautifully-styled HTML document for a digital product.
// This is what users open in a new tab / print to PDF / import into Canva-style tools.
export function buildProductHtmlDocument({ title, markdown, coverDataUrl, subtitle }) {
  const body = mdToHtml(markdown || "");
  const safeTitle = escapeHtml(title || "Digital Product");
  const safeSub = subtitle ? escapeHtml(subtitle) : "";

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${safeTitle}</title>
  <style>
    :root {
      --ink:       #1b1a18;
      --ink-soft:  #4a4844;
      --amber:     #d97706;
      --amber-ink: #1c1302;
      --cream:     #fbf9f4;
      --line:      #e8e2d3;
      --accent:    #1f6b4a;
    }
    * { box-sizing: border-box; }
    html, body { margin:0; padding:0; background:var(--cream); color:var(--ink); }
    body {
      font: 16px/1.7 "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      -webkit-font-smoothing: antialiased;
    }
    .wrap { max-width: 780px; margin: 0 auto; padding: 56px 48px 96px; }
    .cover {
      position: relative;
      margin: -56px -48px 56px;
      padding: 72px 48px 56px;
      background: linear-gradient(180deg, #16120c 0%, #0d0a05 100%);
      color: #fff;
      border-bottom: 1px solid var(--line);
      overflow: hidden;
    }
    .cover::after {
      content: "";
      position: absolute;
      inset: 0;
      background: radial-gradient(1200px 400px at 0% 0%, rgba(217,119,6,0.18), transparent 60%),
                  radial-gradient(900px 300px at 100% 100%, rgba(31,107,74,0.18), transparent 60%);
      pointer-events: none;
    }
    .cover .eyebrow {
      position: relative;
      z-index: 1;
      display: inline-block;
      font: 700 11px/1 "Inter", sans-serif;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: #fbbf24;
      padding: 6px 12px;
      border: 1px solid rgba(251,191,36,0.5);
      border-radius: 999px;
      margin-bottom: 20px;
    }
    .cover h1 {
      position: relative;
      z-index: 1;
      font: 800 clamp(34px, 5vw, 52px)/1.08 "Inter", sans-serif;
      letter-spacing: -0.02em;
      margin: 0 0 14px;
    }
    .cover .sub {
      position: relative;
      z-index: 1;
      font: 400 17px/1.5 "Inter", sans-serif;
      color: #d6d3cc;
      max-width: 560px;
    }
    .cover img {
      position: relative;
      z-index: 1;
      display: block;
      width: 180px;
      height: auto;
      aspect-ratio: 2/3;
      object-fit: cover;
      margin-top: 28px;
      border-radius: 12px;
      border: 1px solid rgba(255,255,255,0.1);
      box-shadow: 0 24px 60px -16px rgba(0,0,0,0.6);
    }
    .doc h1, .doc h2, .doc h3, .doc h4, .doc h5, .doc h6 {
      font-family: "Inter", sans-serif;
      color: var(--ink);
      letter-spacing: -0.01em;
      line-height: 1.2;
      margin: 2.2em 0 0.6em;
    }
    .doc h1 { font-size: 30px; font-weight: 800; padding-bottom: 8px; border-bottom: 2px solid var(--line); }
    .doc h2 { font-size: 24px; font-weight: 800; color: var(--accent); }
    .doc h3 { font-size: 19px; font-weight: 700; }
    .doc h4 { font-size: 16px; font-weight: 700; color: var(--amber); text-transform: uppercase; letter-spacing: 0.06em; }
    .doc p  { margin: 0.9em 0; color: var(--ink-soft); }
    .doc ul, .doc ol { padding-left: 1.3em; margin: 0.9em 0; color: var(--ink-soft); }
    .doc li { margin: 0.35em 0; }
    .doc li::marker { color: var(--amber); }
    .doc strong { color: var(--ink); font-weight: 700; }
    .doc em { color: var(--ink); }
    .doc a { color: var(--amber); text-decoration: underline; text-underline-offset: 3px; }
    .doc blockquote {
      margin: 1.4em 0;
      padding: 14px 20px;
      border-left: 4px solid var(--amber);
      background: #fff6e1;
      border-radius: 0 10px 10px 0;
      color: var(--ink);
      font-style: italic;
    }
    .doc hr { border: 0; border-top: 1px dashed var(--line); margin: 2.4em 0; }
    .doc code {
      background: #f1ece0;
      border-radius: 5px;
      padding: 2px 6px;
      font: 500 14px/1.4 "SFMono-Regular", Menlo, Consolas, monospace;
      color: #5b3a00;
    }
    .doc pre {
      background: #1b1a18;
      color: #eee7d3;
      padding: 16px 18px;
      border-radius: 12px;
      overflow-x: auto;
      font: 500 13.5px/1.55 "SFMono-Regular", Menlo, Consolas, monospace;
    }
    .toolbar {
      position: sticky;
      top: 0;
      z-index: 10;
      background: rgba(251,249,244,0.88);
      backdrop-filter: saturate(1.2) blur(8px);
      border-bottom: 1px solid var(--line);
    }
    .toolbar .inner {
      max-width: 780px;
      margin: 0 auto;
      padding: 10px 16px;
      display: flex; align-items: center; justify-content: space-between; gap: 8px;
      font: 700 11px/1 "Inter", sans-serif;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--ink-soft);
    }
    .btn {
      appearance: none;
      border: 1px solid var(--amber);
      background: var(--amber);
      color: var(--amber-ink);
      padding: 8px 14px;
      border-radius: 999px;
      font: 700 11px/1 "Inter", sans-serif;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      cursor: pointer;
    }
    .btn.secondary { background: transparent; color: var(--ink); border-color: var(--ink-soft); }
    footer {
      margin-top: 72px;
      padding-top: 20px;
      border-top: 1px solid var(--line);
      font: 400 12px/1.5 "Inter", sans-serif;
      color: var(--ink-soft);
      text-align: center;
    }
    @media print {
      .toolbar { display: none !important; }
      body { background: #fff; }
      .cover { margin: 0 0 24px; padding: 32px 0; background: none; color: #111; border-bottom: 2px solid #111; }
      .cover .eyebrow { color: #8a5a00; border-color: #8a5a00; }
      .cover h1, .cover .sub { color: #111 !important; }
      .cover img { width: 140px; box-shadow: none; border: 1px solid #ccc; }
      .wrap { padding: 0 24px; max-width: none; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="inner">
      <span>BuddyPass AI · Product Preview</span>
      <div style="display:flex; gap:8px;">
        <button class="btn secondary" id="copy-rich-btn">Copy to Customize</button>
        <button class="btn secondary" id="download-html-btn">Download</button>
        <button class="btn" onclick="window.print()">Save as PDF</button>
      </div>
    </div>
  </div>

  <script>
    (function(){
      const dlBtn = document.getElementById('download-html-btn');
      if (dlBtn) {
        dlBtn.addEventListener('click', () => {
          const original = dlBtn.textContent;
          try {
            const html = '<!doctype html>' + document.documentElement.outerHTML;
            const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const slug = (document.title || 'product').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'').slice(0,60) || 'product';
            a.href = url;
            a.download = slug + '.html';
            document.body.appendChild(a);
            a.click();
            setTimeout(() => { try { a.remove(); URL.revokeObjectURL(url); } catch(e) {} }, 4000);
            dlBtn.textContent = 'Downloaded';
            setTimeout(() => { dlBtn.textContent = original; }, 2200);
          } catch (e) {
            dlBtn.textContent = 'Blocked — try again';
            setTimeout(() => { dlBtn.textContent = original; }, 2200);
          }
        });
      }
    })();
  </script>

  <script>
    (function(){
      const btn = document.getElementById('copy-rich-btn');
      if (!btn) return;
      btn.addEventListener('click', async () => {
        const original = btn.textContent;
        try {
          const article = document.querySelector('article.doc');
          const cover = document.querySelector('header.cover');
          const wrap = document.createElement('div');
          if (cover) {
            const title = cover.querySelector('h1');
            const sub = cover.querySelector('.sub');
            if (title) { const h = document.createElement('h1'); h.textContent = title.textContent; wrap.appendChild(h); }
            if (sub)   { const p = document.createElement('p'); p.innerHTML = '<em>' + sub.textContent + '</em>'; wrap.appendChild(p); }
          }
          if (article) {
            // Append a clone without the toolbar/cover styling so it pastes clean.
            wrap.appendChild(article.cloneNode(true));
          }
          const html = wrap.innerHTML;
          const plain = wrap.innerText;
          if (navigator.clipboard && window.ClipboardItem) {
            await navigator.clipboard.write([
              new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([plain], { type: 'text/plain' }),
              }),
            ]);
          } else {
            // Fallback: use a contentEditable selection + execCommand.
            const div = document.createElement('div');
            div.contentEditable = 'true';
            div.innerHTML = html;
            div.style.position = 'fixed';
            div.style.left = '-9999px';
            document.body.appendChild(div);
            const range = document.createRange();
            range.selectNodeContents(div);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
            document.execCommand('copy');
            sel.removeAllRanges();
            document.body.removeChild(div);
          }
          btn.textContent = 'Copied — paste into Canva / Docs';
          setTimeout(() => { btn.textContent = original; }, 2200);
        } catch (e) {
          btn.textContent = 'Copy blocked — select + ⌘C';
          setTimeout(() => { btn.textContent = original; }, 2200);
        }
      });
    })();
  </script>

  <div class="wrap">
    <header class="cover">
      <span class="eyebrow">${safeSub || "Digital Product"}</span>
      <h1>${safeTitle}</h1>
      ${safeSub ? `<div class="sub">${safeSub}</div>` : ""}
      ${coverDataUrl ? `<img src="${coverDataUrl}" alt="cover" />` : ""}
    </header>

    <article class="doc">
      ${body}
    </article>

    <footer>
      Generated with BuddyPass AI™ · ${new Date().toLocaleDateString()}
    </footer>
  </div>
</body>
</html>`;
}
